/**
 * pages/SellerDashboard.tsx - Seller Analytics Dashboard
 * Shows listing stats, views, inquiries, response rate, and rating using recharts
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '@/lib/api/users';
import { listingsAPI } from '@/lib/api/listings';
import { messagesAPI } from '@/lib/api/messages';
import { UserStats } from '@/types/user';
import { apiClient } from '@/lib/api-client';
import SEO from '@/components/common/SEO';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import {
  BarChart3,
  Eye,
  MessageSquare,
  TrendingUp,
  Star,
  Package,
  Loader2,
  AlertCircle,
} from 'lucide-react';

const SellerDashboard: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    } else if (user?.role !== 'SELLER' && user?.role !== 'CUSTOMER') {
      // Try to load anyway - seller dashboard is accessible to sellers
    }
    loadStats();
  }, [isAuthenticated, user, navigate]);

  const loadStats = async () => {
    if (!user?.sub) return;
    setLoading(true);
    setError(null);
    try {
      // Get or auto-create seller profile
      let sellerProfile;
      try {
        sellerProfile = await apiClient.get(`/api/sellers/user/${user.sub}`);
      } catch (err: any) {
        if (err?.status === 404 || err?.message?.includes('404')) {
          let realPhone = user.phone;
          try {
            const profile = await apiClient.get('/api/users/me');
            if (profile?.phone) {
              realPhone = profile.phone;
            }
          } catch (pErr) {
            console.warn('Could not fetch user profile for phone fallback:', pErr);
          }

          sellerProfile = await apiClient.post('/api/sellers', {
            userId: user.sub,
            phone: realPhone || '0000000000',
            businessType: 'INDIVIDUAL',
            bio: `Seller profile for ${user.name || user.preferred_username || user.email}`,
            businessName: user.name || 'Individual Seller',
          });
        } else {
          throw err;
        }
      }

      if (!sellerProfile || !sellerProfile.id) {
        throw new Error('Failed to load seller profile information');
      }

      const [statsData, listingsData, conversationsData] = await Promise.all([
        apiClient.get(`/api/sellers/${sellerProfile.id}/stats`),
        listingsAPI.getSellerListings(sellerProfile.id).catch(() => ({ content: [], totalElements: 0 } as any)),
        messagesAPI.getConversations().catch(() => ({ content: [], totalElements: 0 } as any)),
      ]);

      const allListings = listingsData.content || listingsData.items || listingsData.listings || [];
      const totalListings = listingsData.totalElements ?? listingsData.total ?? allListings.length ?? statsData.totalListings ?? 0;
      const totalViews = allListings.reduce((sum: number, l: any) => sum + (l.views || 0), 0);
      const totalInquiries = conversationsData.totalElements ?? conversationsData.total ?? conversationsData.content?.length ?? 0;
      const soldListingsCount = allListings.filter((l: any) => l.status === 'SOLD').length;

      setStats({
        totalListings,
        totalSold: soldListingsCount || statsData.totalSales || 0,
        totalBought: totalInquiries,
        totalSpent: totalViews,
        ratings: statsData.rating || 0.0,
        reviewCount: statsData.reviewCount || 0,
        responsiveness: statsData.responseTime || 'N/A',
      });
    } catch (err: any) {
      console.error('Error loading seller stats:', err);
      setError(err?.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  // Generate mock 30-day trend data for demo
  // In production, this would come from an analytics API
  const generateTrendData = () => {
    const days = [];
    const totalViews = stats?.totalSpent || 0;
    const totalInquiries = stats?.totalBought || 0;
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      const viewsShare = totalViews > 0 
        ? Math.max(1, Math.floor((totalViews / 30) * (0.4 + Math.random() * 1.2))) 
        : 0;
      const inquiriesShare = totalInquiries > 0
        ? Math.max(1, Math.floor((totalInquiries / 30) * (0.3 + Math.random() * 1.4)))
        : 0;

      days.push({
        date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        views: viewsShare,
        inquiries: inquiriesShare,
      });
    }
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
          <p className="text-destructive">{error}</p>
          <button onClick={loadStats} className="mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const trendData = generateTrendData();

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      <SEO title="Seller Dashboard" noindex />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="h-8 w-8 text-primary" />
            Seller Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Track your listing performance and engagement</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Package}
            label="Total Listings"
            value={stats?.totalListings?.toString() || '0'}
            color="blue"
          />
          <StatCard
            icon={Eye}
            label="Total Views"
            value={(stats?.totalSpent || 0).toString()} // Using totalSpent as proxy for views (backend field mismatch)
            color="purple"
          />
          <StatCard
            icon={MessageSquare}
            label="Inquiries"
            value={(stats?.totalBought || 0).toString()} // Proxy field
            color="green"
          />
          <StatCard
            icon={Star}
            label="Avg Rating"
            value={stats?.ratings?.toFixed(1) || '0.0'}
            color="amber"
          />
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <MetricCard
            label="Response Time"
            value={stats?.responsiveness ? stats.responsiveness.toString() : 'N/A'}
            description="Average time to respond to inquiries"
            icon={TrendingUp}
          />
          <MetricCard
            label="Review Score"
            value={`${stats?.ratings?.toFixed(1) || '0.0'} / 5`}
            description={`Based on ${stats?.reviewCount || 0} reviews`}
            icon={Star}
          />
          <MetricCard
            label="Total Sold"
            value={stats?.totalSold?.toString() || '0'}
            description="Items sold successfully"
            icon={Package}
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Views Trend */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Views (Last 30 Days)
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  interval={4}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="views"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Inquiries Trend */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-600" />
              Inquiries (Last 30 Days)
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  interval={4}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="inquiries" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground text-sm mb-4">
            Want more visibility? Promote your listings to reach more buyers.
          </p>
          <button
            onClick={() => navigate('/post')}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition font-semibold text-sm"
          >
            <Package className="h-4 w-4" />
            Post New Listing
          </button>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  icon: React.ElementType;
  label: string;
  value: string;
  color: 'blue' | 'purple' | 'green' | 'amber';
}> = ({ icon: Icon, label, value, color }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  return (
    <div className={`rounded-xl border p-4 sm:p-5 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
};

// Metric Card Component
const MetricCard: React.FC<{
  label: string;
  value: string;
  description: string;
  icon: React.ElementType;
}> = ({ label, value, description, icon: Icon }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </div>
    <p className="text-2xl font-bold text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{description}</p>
  </div>
);

export default SellerDashboard;